import { useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Users, Settings, Palette, Globe, LogOut, Megaphone,
  ChevronLeft, ChevronRight, Search, CreditCard, Bell, Briefcase, History, BarChart2, Sparkles,
  Bot, Activity, FileText, KeyRound,
  GitMerge, ShieldAlert, BookOpen, Clock, Rocket,
  Zap, GitBranch, FlaskConical,
  RefreshCw, Tag, CalendarClock,
  FileBarChart, Mail, Gauge,
  Flag, Database, Layers, Bug, CircuitBoard, Timer,
  Webhook, Inbox, Monitor,
  Calculator, Smartphone, ShieldCheck, Swords,
  Image as ImageIcon, Map as MapIcon,
  Wallet, Workflow, Archive,
  HeartPulse, Send,
  Calendar as CalendarIcon, Slack as SlackIcon, Code2 as Code2Icon,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth, userHasAdminSystem } from '@/store/auth';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { cn } from '@/lib/utils';
import i18n from '@/i18n';
import { SkipLink } from '@/components/i18n/SkipLink';
import { AnnouncementBanner } from '@/components/comm/AnnouncementBanner';
import { MessageCircle } from 'lucide-react';

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  // Spatie roles come through as `{ name }[]` on the auth user. If the user
  // has no roles (dev / fresh seed), default to showing the Billing link so
  // the owner-only gate in BillingPage itself is the authoritative check.
  const roles = user?.roles ?? [];
  const isOwner = roles.length === 0 || roles.some((r) => r.name === 'owner' || r.name === 'admin');
  const isAdminSystem = userHasAdminSystem(user);

  // Polish pass — language switcher; persists to /user/preferences best-effort
  const [lang, setLang] = useState<string>(() => i18n.language?.slice(0, 2) || 'pl');
  function changeLanguage(next: string) {
    setLang(next);
    i18n.changeLanguage(next);
    try {
      localStorage.setItem('i18nextLng', next);
    } catch {
      // ignore quota errors
    }
    api.put('/user/preferences', { locale: next }).catch(() => {
      // backend persistence is best-effort; localStorage is the source of truth
    });
  }

  // Poll unread count so the Alerty link in the sidebar can show a badge
  // (shares query key with NotificationBell for a single source of truth).
  const { data: unreadData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: async () => (await api.get('/notifications/unread-count')).data as { count: number },
    refetchInterval: 30_000,
  });
  const unreadCount = unreadData?.count ?? 0;

  // comm_ — unread inbox message badge
  const { data: commUnreadData } = useQuery({
    queryKey: ['comm-unread-count'],
    queryFn: async () => (await api.get('/messages/unread-count')).data as { count: number },
    refetchInterval: 30_000,
    retry: false,
  });
  const commUnread = commUnreadData?.count ?? 0;

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/accounts', icon: Megaphone, label: t('nav.accounts') },
    { to: '/alerts', icon: Bell, label: 'Alerty', badge: unreadCount },
    { to: '/insights', icon: Sparkles, label: t('nav.insights_hub', { defaultValue: 'Centrum analiz' }) },
    // Agencja section (P1.7 — panel właściciela agencji, spec 3.5.10)
    ...(isOwner
      ? [{ to: '/agency/overview', icon: Briefcase, label: t('nav.agency_overview', { defaultValue: 'Przegląd agencji' }) }]
      : []),
    ...(isOwner
      ? [{ to: '/agency/metrics', icon: BarChart2, label: t('nav.agency_metrics', { defaultValue: 'Metryki agencji' }) }]
      : []),
    { to: '/agency/team', icon: Users, label: t('nav.team') },
    // team_ — new team management nav (visible to owners/admins)
    { to: '/team', icon: Users, label: t('nav.team_main', { defaultValue: 'Team' }) },
    ...(isOwner
      ? [{ to: '/team/sub-agencies', icon: GitBranch, label: t('nav.team_sub_agencies', { defaultValue: 'Sub-agencies' }) }]
      : []),
    ...(isOwner
      ? [{ to: '/team/permissions', icon: ShieldCheck, label: t('nav.team_permissions', { defaultValue: 'Permissions' }) }]
      : []),
    ...(isOwner
      ? [{ to: '/team/migrate-account', icon: GitBranch, label: t('nav.team_migrate_account', { defaultValue: 'Migrate Account' }) }]
      : []),
    { to: '/agency/branding', icon: Palette, label: t('nav.branding') },
    { to: '/agency/domains', icon: Globe, label: t('nav.domains') },
    ...(isOwner ? [{ to: '/billing', icon: CreditCard, label: t('nav.billing', { defaultValue: 'Billing' }) }] : []),
    ...(isOwner ? [{ to: '/audit-log', icon: History, label: t('nav.audit_log', { defaultValue: 'Audit log' }) }] : []),
    { to: '/settings', icon: Settings, label: t('nav.settings') },
    // Ops hub — cross-account global pages (appended last to avoid conflicts)
    { to: '/copilot', icon: Bot, label: t('nav.ops_copilot', { defaultValue: 'AI Copilot' }) },
    { to: '/activity', icon: Activity, label: t('nav.ops_activity', { defaultValue: 'Aktywność' }) },
    { to: '/reports', icon: FileText, label: t('nav.ops_reports', { defaultValue: 'Raporty' }) },
    { to: '/settings/security', icon: KeyRound, label: t('nav.ops_security', { defaultValue: 'Bezpieczeństwo' }) },
    { to: '/campaigns/new', icon: Rocket, label: t('nav.ops_new_campaign', { defaultValue: 'Nowa kampania' }) },
    { to: '/mutations', icon: GitMerge, label: t('nav.ops_mutations', { defaultValue: 'Zmiany' }) },
    { to: '/findings', icon: ShieldAlert, label: t('nav.ops_findings', { defaultValue: 'Diagnostyka' }) },
    { to: '/knowledge', icon: BookOpen, label: t('nav.ops_knowledge', { defaultValue: 'Baza wiedzy' }) },
    ...(isAdminSystem
      ? [{ to: '/knowledge/admin', icon: BookOpen, label: t('nav.pol_kb_admin', { defaultValue: 'Admin bazy wiedzy' }) }]
      : []),
    { to: '/settings/gdpr', icon: ShieldAlert, label: t('nav.pol_gdpr', { defaultValue: 'RODO' }) },
    { to: '/settings/notifications', icon: Bell, label: t('nav.ops_notifications', { defaultValue: 'Powiadomienia' }) },
    ...(isOwner ? [{ to: '/settings/sla', icon: Clock, label: t('nav.ops_sla', { defaultValue: 'Polityki SLA' }) }] : []),
    // Auto-optimization (P1 autoopt_) — appended last to avoid conflicts
    { to: '/autoopt', icon: Zap, label: t('nav.autoopt_dashboard', { defaultValue: 'Auto-optymalizacja' }) },
    { to: '/autoopt/attribution', icon: GitBranch, label: t('nav.autoopt_attribution', { defaultValue: 'Atrybucja' }) },
    { to: '/autoopt/experiments', icon: FlaskConical, label: t('nav.autoopt_experiments', { defaultValue: 'Auto-promocja A/B' }) },
    // Bulk ops + productivity (bulk_) — appended last to avoid conflicts
    { to: '/bulk/sync', icon: RefreshCw, label: t('nav.bulk_sync', { defaultValue: 'Synchronizacja masowa' }) },
    { to: '/bulk/tags', icon: Tag, label: t('nav.bulk_tags', { defaultValue: 'Etykiety kont' }) },
    { to: '/bulk/schedules', icon: CalendarClock, label: t('nav.bulk_schedules', { defaultValue: 'Harmonogramy sync' }) },
    // Reporting + analytics + email templates (rpt_) — appended last
    { to: '/rpt/custom', icon: FileBarChart, label: t('nav.rpt_custom_reports', { defaultValue: 'Custom raporty' }) },
    { to: '/rpt/email-templates', icon: Mail, label: t('nav.rpt_email_templates', { defaultValue: 'Szablony email' }) },
    ...(isAdminSystem
      ? [{ to: '/rpt/performance', icon: Gauge, label: t('nav.rpt_performance', { defaultValue: 'Wydajność' }) }]
      : []),
    // Admin tooling (adm_) — gated by admin.system, appended last
    ...(isAdminSystem
      ? [
          { to: '/admin/jobs', icon: CircuitBoard, label: t('nav.adm_jobs', { defaultValue: 'Admin · Kolejka' }) },
          { to: '/admin/errors', icon: Bug, label: t('nav.adm_errors', { defaultValue: 'Admin · Błędy' }) },
          { to: '/admin/db', icon: Database, label: t('nav.adm_db', { defaultValue: 'Admin · Baza' }) },
          { to: '/admin/feature-flags', icon: Flag, label: t('nav.adm_feature_flags', { defaultValue: 'Admin · Feature flags' }) },
          { to: '/admin/plans', icon: Layers, label: t('nav.adm_plans', { defaultValue: 'Admin · Plany' }) },
          { to: '/admin/slow-queries', icon: Timer, label: t('nav.adm_slow_queries', { defaultValue: 'Admin · Slow queries' }) },
        ]
      : []),
    // Integrations (intg_) — appended last to avoid conflicts
    { to: '/intg/api-keys', icon: KeyRound, label: t('nav.intg_api_keys', { defaultValue: 'API Keys' }) },
    { to: '/intg/webhooks', icon: Webhook, label: t('nav.intg_webhooks', { defaultValue: 'Webhooki' }) },
    { to: '/intg/incoming-webhooks', icon: Inbox, label: t('nav.intg_incoming_webhooks', { defaultValue: 'Webhooki przychodzące' }) },
    { to: '/intg/sessions', icon: Monitor, label: t('nav.intg_sessions', { defaultValue: 'Sesje' }) },
    // Bidding & Optimization (bid_) — appended last to avoid conflicts
    { to: '/bid/rules', icon: Zap, label: t('nav.bid_rules', { defaultValue: 'Bidding Rules' }) },
    { to: '/bid/day-parting', icon: CalendarClock, label: t('nav.bid_day_parting', { defaultValue: 'Day-parting' }) },
    { to: '/bid/search-query-mining', icon: Search, label: t('nav.bid_search_query_mining', { defaultValue: 'Search Query Mining' }) },
    { to: '/bid/negative-lists', icon: BookOpen, label: t('nav.bid_negative_lists', { defaultValue: 'Negative Lists' }) },
    { to: '/bid/simulator', icon: Calculator, label: t('nav.bid_simulator', { defaultValue: 'Bid Simulator' }) },
    // Cross-account analytics (multi_) — appended last
    { to: '/multi/pacing', icon: Gauge, label: t('nav.multi_pacing', { defaultValue: 'Multi-account Pacing' }) },
    { to: '/multi/benchmarks', icon: BarChart2, label: t('nav.multi_benchmarks', { defaultValue: 'Benchmarks' }) },
    { to: '/multi/industry-benchmarks', icon: Layers, label: t('nav.multi_industry_benchmarks', { defaultValue: 'Industry Benchmarks' }) },
    { to: '/multi/spend-anomaly', icon: Activity, label: t('nav.multi_spend_anomaly', { defaultValue: 'Spend Anomaly' }) },
    { to: '/multi/calendar', icon: CalendarClock, label: t('nav.multi_calendar', { defaultValue: 'Change Calendar' }) },
    { to: '/multi/rollforward', icon: GitBranch, label: t('nav.multi_rollforward', { defaultValue: 'Rollforward' }) },
    // Compliance, Page Speed, Competitors, Landing A/B (comp_)
    { to: '/comp/checker', icon: ShieldCheck, label: t('nav.comp_checker', { defaultValue: 'Compliance Checker' }) },
    { to: '/comp/page-speed', icon: Gauge, label: t('nav.comp_page_speed', { defaultValue: 'Page Speed' }) },
    { to: '/comp/competitors', icon: Swords, label: t('nav.comp_competitors', { defaultValue: 'Konkurenci' }) },
    { to: '/comp/landing-ab', icon: FlaskConical, label: t('nav.comp_landing_ab', { defaultValue: 'Landing A/B' }) },
    // PWA shell (pwa_) — under Settings group
    { to: '/pwa/settings', icon: Smartphone, label: t('nav.pwa_settings', { defaultValue: 'Aplikacja PWA' }) },
    // Creative tools (crv_) — appended last to avoid conflicts
    { to: '/crv/ai-images', icon: ImageIcon, label: t('nav.crv_ai_images', { defaultValue: 'AI Images' }) },
    { to: '/crv/datastudio', icon: BarChart2, label: t('nav.crv_datastudio', { defaultValue: 'Looker Studio' }) },
    { to: '/crv/conversion-paths', icon: GitBranch, label: t('nav.crv_conversion_paths', { defaultValue: 'Ścieżki konwersji' }) },
    { to: '/crv/geo-heatmap', icon: MapIcon, label: t('nav.crv_geo_heatmap', { defaultValue: 'Geo Heatmapa' }) },
    { to: '/crv/demographics', icon: Users, label: t('nav.crv_demographics', { defaultValue: 'Demografia Deep' }) },
    // Shared budgets, automation, backup (share_)
    { to: '/share/budgets', icon: Wallet, label: t('nav.share_budgets', { defaultValue: 'Wspólne budżety' }) },
    { to: '/share/automation', icon: Workflow, label: t('nav.share_automation', { defaultValue: 'Reguły automatyzacji' }) },
    ...(isOwner ? [{ to: '/share/backup', icon: Archive, label: t('nav.share_backup', { defaultValue: 'Backup agencji' }) }] : []),
    // edu_ — education hub
    { to: '/edu/tutorials', icon: BookOpen, label: t('nav.edu_tutorials', { defaultValue: 'Kurs AIAds' }) },
    { to: '/edu/best-practices', icon: BookOpen, label: t('nav.edu_best_practices', { defaultValue: 'Dobre praktyki' }) },
    { to: '/edu/glossary', icon: BookOpen, label: t('nav.edu_glossary', { defaultValue: 'Słowniczek' }) },
    { to: '/edu/demo', icon: Sparkles, label: t('nav.edu_demo', { defaultValue: 'Demo' }) },
    // help_ — help center pages
    { to: '/help/changelog', icon: History, label: t('nav.help_changelog', { defaultValue: 'Changelog' }) },
    { to: '/help/roadmap', icon: Rocket, label: t('nav.help_roadmap', { defaultValue: 'Roadmap' }) },
    { to: '/help/videos', icon: BookOpen, label: t('nav.help_videos', { defaultValue: 'Wideo' }) },
    { to: '/help/features', icon: Sparkles, label: t('nav.help_features', { defaultValue: 'Sugestie funkcji' }) },
    ...(isAdminSystem ? [{ to: '/help/bug-reports', icon: Bug, label: t('nav.help_bug_reports', { defaultValue: 'Zgłoszone błędy' }) }] : []),
    // comm_ — communications hub (inbox, announcements, email aliases)
    { to: '/comm/inbox', icon: MessageCircle, label: t('nav.comm_inbox', { defaultValue: 'Skrzynka' }), badge: commUnread },
    ...(isOwner ? [{ to: '/comm/announcements', icon: Megaphone, label: t('nav.comm_announcements', { defaultValue: 'Ogłoszenia' }) }] : []),
    ...(isOwner ? [{ to: '/comm/email-aliases', icon: Mail, label: t('nav.comm_email_aliases', { defaultValue: 'Aliasy email' }) }] : []),
    // pub_ — public docs + integrations meta
    { to: '/api-docs', icon: FileText, label: t('nav.pub_api_docs', { defaultValue: 'API Docs' }) },
    { to: '/embed/builder', icon: Monitor, label: t('nav.pub_embed_builder', { defaultValue: 'Embed Builder' }) },
    { to: '/intg/api-usage', icon: Activity, label: t('nav.pub_api_usage', { defaultValue: 'API Usage' }) },
    { to: '/intg/webhook-events', icon: Webhook, label: t('nav.pub_webhook_events', { defaultValue: 'Webhook Events' }) },
    // int2_ — calendar, Slack commands, webhook templates, integration recipes, embed builder
    { to: '/int2/calendar', icon: CalendarIcon, label: t('nav.int2_calendar', { defaultValue: 'Kalendarz' }) },
    { to: '/int2/slack', icon: SlackIcon, label: t('nav.int2_slack', { defaultValue: 'Slack' }) },
    { to: '/int2/webhook-templates', icon: Inbox, label: t('nav.int2_webhook_templates', { defaultValue: 'Szablony webhooków' }) },
    { to: '/int2/recipes', icon: BookOpen, label: t('nav.int2_recipes', { defaultValue: 'Przepisy integracji' }) },
    { to: '/int2/embed-builder', icon: Code2Icon, label: t('nav.int2_embed_builder', { defaultValue: 'Embed Builder+' }) },
    // leg_ — legal / compliance links for authenticated users
    { to: '/settings/recovery-codes', icon: KeyRound, label: t('nav.leg_recovery_codes', { defaultValue: 'Kody odzyskiwania' }) },
    { to: '/settings/data-retention', icon: Archive, label: t('nav.leg_data_retention', { defaultValue: 'Retencja danych' }) },
    // biz_ — business intelligence (gated by admin.system; telemetry is user-settings)
    ...(isAdminSystem
      ? [
          { to: '/admin/biz/revenue', icon: BarChart2, label: t('nav.biz_revenue', { defaultValue: 'Biz · Przychody' }) },
          { to: '/admin/biz/usage', icon: Activity, label: t('nav.biz_usage', { defaultValue: 'Biz · Użycie' }) },
          { to: '/admin/biz/funnels', icon: GitBranch, label: t('nav.biz_funnels', { defaultValue: 'Biz · Lejki' }) },
          { to: '/admin/biz/customer-health', icon: HeartPulse, label: t('nav.biz_customer_health', { defaultValue: 'Biz · Kondycja klientów' }) },
          { to: '/admin/biz/paywall-hits', icon: ShieldAlert, label: t('nav.biz_paywall', { defaultValue: 'Biz · Paywall' }) },
        ]
      : []),
    { to: '/settings/telemetry', icon: ShieldCheck, label: t('nav.biz_telemetry', { defaultValue: 'Telemetria' }) },
    // mon_ — admin monitoring dashboards (gated by admin.system)
    ...(isAdminSystem
      ? [
          { to: '/admin/mon/system-health', icon: HeartPulse, label: t('nav.mon_system_health', { defaultValue: 'Mon · Kondycja systemu' }) },
          { to: '/admin/mon/email-delivery', icon: Send, label: t('nav.mon_email_delivery', { defaultValue: 'Mon · Email' }) },
          { to: '/admin/mon/webhooks', icon: Webhook, label: t('nav.mon_webhooks', { defaultValue: 'Mon · Webhooki' }) },
          { to: '/admin/mon/job-health', icon: Activity, label: t('nav.mon_job_health', { defaultValue: 'Mon · Joby' }) },
          { to: '/admin/mon/circuit-breakers', icon: ShieldAlert, label: t('nav.mon_circuit_breakers', { defaultValue: 'Mon · Circuit breakers' }) },
          { to: '/admin/mon/errors', icon: Bug, label: t('nav.mon_errors', { defaultValue: 'Mon · Crash reports' }) },
          { to: '/admin/mon/slow-requests', icon: Timer, label: t('nav.mon_slow_requests', { defaultValue: 'Mon · Slow requests' }) },
        ]
      : []),
  ] as Array<{ to: string; icon: typeof LayoutDashboard; label: string; badge?: number }>;

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-950">
      <SkipLink />
      {/* Sidebar */}
      <aside
        className={cn(
          'bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-all duration-300 ease-in-out shrink-0',
          collapsed ? 'w-[68px]' : 'w-64',
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white font-bold text-sm shrink-0 shadow-sm">
              A
            </div>
            {!collapsed && (
              <div className="fade-in">
                <div className="text-sm font-semibold dark:text-white truncate">{user?.agency?.name ?? 'AIAds'}</div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">{t('app.tagline')}</div>
              </div>
            )}
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {navItems.map(({ to, icon: Icon, label, badge }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150 relative',
                  collapsed && 'justify-center px-0',
                  isActive
                    ? 'bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-400 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white',
                )
              }
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && <span className="truncate flex-1">{label}</span>}
              {!!badge && badge > 0 && (
                <span
                  className={cn(
                    'h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center',
                    collapsed && 'absolute top-1 right-1',
                  )}
                >
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Collapse toggle */}
        <div className="px-2 py-2">
          <button
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
            className="flex items-center justify-center w-full rounded-lg p-2 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition focus:outline-none focus:ring-2 focus:ring-brand-400"
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}
          </button>
        </div>

        {/* User + Logout */}
        <div className="p-2 border-t border-slate-200 dark:border-slate-800">
          {!collapsed && user && (
            <div className="px-3 py-2 mb-1">
              <p className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{user.name}</p>
              <p className="text-[11px] text-slate-400 truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={() => logout()}
            title={collapsed ? t('nav.logout') : undefined}
            className={cn(
              'flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm text-slate-600 dark:text-slate-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition',
              collapsed && 'justify-center px-0',
            )}
          >
            <LogOut size={18} className="shrink-0" />
            {!collapsed && t('nav.logout')}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-14 shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 gap-4">
          {/* Search — also acts as Cmd+K hint */}
          <div className="flex-1 max-w-md">
            <button
              type="button"
              onClick={() => {
                // Synthesise Cmd+K so the global CommandPalette opens
                const ev = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true });
                window.dispatchEvent(ev);
              }}
              className="relative w-full flex items-center text-left"
              aria-label={t('pol.search_hint', { defaultValue: 'Szybkie wyszukiwanie' })}
              title={t('pol.search_hint', { defaultValue: 'Szybkie wyszukiwanie' })}
            >
              <Search size={16} aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <span className="input pl-9 pr-12 py-1.5 text-sm w-full bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500">
                {t('pol.search_hint', { defaultValue: 'Szybkie wyszukiwanie' })}…
              </span>
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-600">
                ⌘K
              </kbd>
            </button>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <select
              value={lang}
              onChange={(e) => changeLanguage(e.target.value)}
              className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-slate-600 dark:text-slate-300"
              aria-label={t('pol.language', { defaultValue: 'Język' })}
              title={t('pol.language', { defaultValue: 'Język' })}
            >
              <option value="pl">PL</option>
              <option value="en">EN</option>
              <option value="de">DE</option>
              <option value="uk">UK</option>
              <option value="cs">CS</option>
            </select>
            <NotificationBell />
            <ThemeToggle />
          </div>
        </header>

        {/* comm_ — agency announcement banner (top of app) */}
        <AnnouncementBanner />

        {/* Page content */}
        <div id="main-content" role="main" tabIndex={-1} className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
